import React, { useState } from 'react';
import { UserIcon, KeyIcon, EyeIcon, EyeOffIcon, CheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
interface ProfileProps {
  username: string;
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
}
export function Profile({
  username,
  onChangePassword
}: ProfileProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Udfyld venligst alle felter');
      return;
    }
    if (newPassword.length < 6) {
      setError('Ny adgangskode skal være mindst 6 tegn');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Nye adgangskoder matcher ikke');
      return;
    }
    if (currentPassword === newPassword) {
      setError('Ny adgangskode skal være forskellig fra den nuværende');
      return;
    }
    setIsLoading(true);
    try {
      const result = await onChangePassword(currentPassword, newPassword);
      if (result) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('Nuværende adgangskode er forkert');
      }
    } catch (err) {
      setError('Der opstod en fejl. Prøv igen.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="max-w-2xl mx-auto" data-id="element-1639">
      <header className="mb-8" data-id="element-1640">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1641">Profil</h1>
        <p className="text-stone-600" data-id="element-1642">Administrer dine kontooplysninger</p>
      </header>

      <div className="space-y-6" data-id="element-1643">
        {/* Username Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1644">
          <div className="flex items-center gap-3 mb-4" data-id="element-1645">
            <div className="p-2 bg-teal-100 rounded-lg" data-id="element-1646">
              <UserIcon className="w-5 h-5 text-teal-700" data-id="element-1647" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900" data-id="element-1648">Brugernavn</h2>
          </div>

          <div className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-1649">
            <p className="text-sm text-stone-500 mb-1" data-id="element-1650">Dit brugernavn</p>
            <p className="text-lg font-medium text-stone-900" data-id="element-1651">{username}</p>
            <p className="text-xs text-stone-400 mt-2" data-id="element-1652">
              Brugernavnet kan ikke ændres
            </p>
          </div>
        </motion.div>

        {/* Change Password Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1653">
          <div className="flex items-center gap-3 mb-6" data-id="element-1654">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1655">
              <KeyIcon className="w-5 h-5 text-blue-700" data-id="element-1656" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900" data-id="element-1657">
              Skift adgangskode
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1658">
            {/* Current Password */}
            <div data-id="element-1659">
              <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1660">
                Nuværende adgangskode
              </label>
              <div className="relative" data-id="element-1661">
                <input type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full px-4 py-2.5 pr-12 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" placeholder="Indtast nuværende adgangskode" data-id="element-1662" />
                <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors" data-id="element-1663">
                  {showCurrentPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-1664" /> : <EyeIcon className="w-5 h-5" data-id="element-1665" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div data-id="element-1666">
              <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1667">
                Ny adgangskode
              </label>
              <div className="relative" data-id="element-1668">
                <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-2.5 pr-12 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" placeholder="Indtast ny adgangskode (min. 6 tegn)" data-id="element-1669" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors" data-id="element-1670">
                  {showNewPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-1671" /> : <EyeIcon className="w-5 h-5" data-id="element-1672" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div data-id="element-1673">
              <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-1674">
                Bekræft ny adgangskode
              </label>
              <div className="relative" data-id="element-1675">
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 pr-12 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" placeholder="Indtast ny adgangskode igen" data-id="element-1676" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors" data-id="element-1677">
                  {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-1678" /> : <EyeIcon className="w-5 h-5" data-id="element-1679" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-1680">
                <p className="text-sm text-red-700" data-id="element-1681">{error}</p>
              </motion.div>}

            {/* Success Message */}
            {success && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-id="element-1682">
                <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-1683" />
                <p className="text-sm text-emerald-700" data-id="element-1684">
                  Adgangskode ændret succesfuldt!
                </p>
              </motion.div>}

            {/* Submit Button */}
            <div className="pt-2" data-id="element-1685">
              <Button type="submit" variant="primary" disabled={isLoading} className="w-full" data-id="element-1686">
                {isLoading ? 'Opdaterer...' : 'Skift adgangskode'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>;
}